import React, { useState, useEffect, useRef } from 'react';
import './LandingPage.css';
import { useLang, LangSwitch, LANDING, tr } from './lib/i18n';

const IMG = process.env.PUBLIC_URL + '/assets/AfriTrip Landing Page_img';
const ICO = process.env.PUBLIC_URL + '/assets/AfriTrip Landing Page_icon';
const LOGO_COLOR = `${IMG}/LOGO-FLYAFRITRIP 1-2.png`;

const SectionBadge = ({ text }) => (
  <div className="section-badge">
    <img src={`${IMG}/stars.png`} alt="" className="section-badge-star-img" />
    <span>{text}</span>
  </div>
);

const CheckItem = ({ text }) => (
  <div className="check-item">
    <img src={`${ICO}/check-circle.png`} alt="" />
    <span>{text}</span>
  </div>
);

const MaxItBadge = ({ className, small }) => (
  <div className={`maxit-badge ${className || ''}`}>
    <img src={`${IMG}/max-img.png`} alt="MaxIt" className="maxit-badge-icon-img" />
    <div className="maxit-badge-label">
      <span className="maxit-badge-small">{small}</span>
      <span className="maxit-badge-big">MaxIt App</span>
    </div>
  </div>
);

const PhoneCard = ({ src, alt, isPhoto }) => (
  <div className="phone-card">
    <div className="phone-card-grid">
      {Array.from({ length: 9 }).map((_, i) => (
        <div key={i} className="phone-card-cell" />
      ))}
    </div>
    {isPhoto ? (
      <img src={src} alt={alt} className="phone-card-photo" />
    ) : (
      <div className="phone-frame">
        <div className="phone-screen">
          <img src={src} alt={alt} />
        </div>
      </div>
    )}
  </div>
);

const FeatureSection = ({ badge, title, titleColor, body, checks, image, imageAlt, reverse, isPhoto }) => (
  <div className={`feature-row ${reverse ? 'feature-row--reverse' : ''}`}>
    <PhoneCard src={image} alt={imageAlt} isPhoto={isPhoto} />
    <div className="feature-text">
      <SectionBadge text={badge} />
      <h2 style={titleColor ? { color: titleColor } : undefined}>{title}</h2>
      <p>{body}</p>
      <div className="check-list">
        {checks.map((c, i) => <CheckItem key={i} text={c} />)}
      </div>
    </div>
  </div>
);

// Text lives in lib/i18n.js (LANDING); only the non-text parts are here.
const FEATURE_META = [
  { image: `${IMG}/source/Payment.png`, reverse: false },
  { image: `${IMG}/source/Search results.png`, reverse: true },
  { image: `${IMG}/source/Trip dwetails.png`, reverse: false },
  { image: `${IMG}/source/Upgrades.png`, reverse: true },
  { image: `${IMG}/source/Trip details.png`, reverse: false },
  { image: `${IMG}/assistance-img.png`, reverse: true, isPhoto: true }
];

const TESTIMONIAL_META = [
  { name: 'Aminata K.', avatar: `${IMG}/Rectangle 18-2.png` },
  { name: 'Moussa D.', avatar: `${IMG}/Rectangle 18.png` },
  { name: 'Sarah N.', avatar: `${IMG}/Rectangle 18-1.png` },
  { name: 'Kofi A.', avatar: `${IMG}/Rectangle 18.png` },
  { name: 'Fatou B.', avatar: `${IMG}/Rectangle 18-2.png` },
  { name: 'Ibrahima S.', avatar: `${IMG}/Rectangle 18-1.png` }
];

const COUNTRY_META = [
  { key: 'sn', flag: '🇸🇳', code: '+221', email: 'senegal@email.com', phone: '0000 0000 00' },
  { key: 'ml', flag: '🇲🇱', code: '+223', email: 'mali@email.com', phone: '0000 0000 00' },
  { key: 'ci', flag: '🇨🇮', code: '+225', email: 'cotedivoire@email.com', phone: '0000 0000 00' }
];

function useVisibleCount() {
  const getCount = () => {
    if (typeof window === 'undefined') return 3;
    if (window.innerWidth <= 767) return 1;
    if (window.innerWidth <= 1024) return 2;
    return 3;
  };
  const [count, setCount] = useState(getCount);
  useEffect(() => {
    const handler = () => setCount(getCount());
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return count;
}

export default function LandingPage() {
  const lang = useLang();
  const C = LANDING[lang];
  useEffect(() => { document.title = C.docTitle; }, [C]);
  const [openFaq, setOpenFaq] = useState(null);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const visibleCount = useVisibleCount();
  useEffect(() => { setTestimonialIndex(0); }, [visibleCount]);
  const canPrev = testimonialIndex > 0;
  const canNext = testimonialIndex + visibleCount < TESTIMONIAL_META.length;
  const [focusedField, setFocusedField] = useState('country');
  const [countryOpen, setCountryOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_META[0]);
  const countryRef = useRef(null);
  const touchStartX = useRef(null);


  useEffect(() => {
    const handleClickOutside = (e) => {
      if (countryRef.current && !countryRef.current.contains(e.target)) {
        setCountryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && canNext) setTestimonialIndex(i => i + 1);
      if (diff < 0 && canPrev) setTestimonialIndex(i => i - 1);
    }
    touchStartX.current = null;
  };

  return (
    <div className="app">
      {/* ── NAVBAR ── */}
      <nav className="navbar">
        <div className="navbar-inner">
          <img src={LOGO_COLOR} alt="FlyAfritrip" className="nav-logo" />
          <div className="nav-links">
            <a href="#features">{tr('navFeatures')}</a>
            <a href="#security">{tr('navSecurity')}</a>
            <a href="#contact">{tr('navContact')}</a>
            <a href={`${process.env.PUBLIC_URL}/booking`}>{tr('navFind')}</a>
          </div>
          <LangSwitch />
          <a href="#download" className="btn-nav">{tr('navDownload')}</a>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="hero">
        <img src={process.env.PUBLIC_URL + "/assets/BG.png"} alt="" className="hero-bg" aria-hidden="true" />
        <div className="hero-text">
          <h1 className="hero-title">
            {C.heroA}<br />
            {C.heroB}{' '}
            <img src={LOGO_COLOR} alt="FlyAfritrip" className="hero-logo-inline" />{' '}
            {C.heroC}
          </h1>
          <p className="hero-sub">
            {C.heroSub}
          </p>
          <a href="#download"><MaxItBadge small={C.maxitSmall} /></a>
        </div>
        <div className="hero-visual">
          <div className="hero-phone-wrapper">
            <img src={`${ICO}/Phone-hero.png`} alt={C.phoneAlt} className="hero-phone" />
          </div>
        </div>
      </section>

      {/* ── TRUST BADGES ── */}
      <section className="badges-section" id="security">
        <div className="container">
          <div className="badges-grid">
            {[
              { icon: `${ICO}/shield-tick.png`, label: C.badges[0] },
              { icon: `${ICO}/headphones-01.png`, label: C.badges[1] },
              { icon: `${ICO}/shield-tick.png`, label: C.badges[2] },
              { icon: `${ICO}/ticket-01.png`, label: C.badges[3] },
            ].map((b, i) => (
              <div key={i} className="badge-card">
                <img src={b.icon} alt="" className="badge-icon" />
                <span>{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES (6 rows) ── */}
      <section className="features-section" id="features">
        <div className="container">
          {C.features.map((f, i) => (
            <FeatureSection key={i} {...FEATURE_META[i]} {...f} />
          ))}
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-label-center">
            <div className="section-badge">
              <img src={`${IMG}/stars.png`} alt="" className="section-badge-star-img" />
              <span>{C.testimonialsBadge}</span>
            </div>
          </div>
          <h2 className="section-title center">{C.testimonialsTitleA}<br />{C.testimonialsTitleB}</h2>
          <div
            className="testimonials-grid"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {TESTIMONIAL_META.slice(testimonialIndex, testimonialIndex + visibleCount).map((t, i) => (
              <div key={testimonialIndex + i} className="testimonial-card">
                <div className="testimonial-header">
                  <div className="testimonial-avatar-wrap">
                    <img src={t.avatar} alt={t.name} className="testimonial-avatar" />
                  </div>
                  <div>
                    <div className="testimonial-name">{t.name}</div>
                    <div className="testimonial-location">{C.locations[testimonialIndex + i]}</div>
                  </div>
                </div>
                <div className="stars">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <img key={j} src={`${ICO}/star-01.png`} alt="★" className="star" />
                  ))}
                </div>
                <p className="testimonial-text">{C.testimonials[testimonialIndex + i]}</p>
              </div>
            ))}
          </div>
          <div className="carousel-nav">
            <button
              className={`carousel-btn${!canPrev ? ' carousel-btn--disabled' : ''}`}
              aria-label={C.prev}
              onClick={() => canPrev && setTestimonialIndex(i => i - 1)}
            >
              <img src={`${ICO}/chevron-right.png`} alt="" className="chevron-flip" />
            </button>
            <button
              className={`carousel-btn${!canNext ? ' carousel-btn--disabled' : ''}`}
              aria-label={C.next}
              onClick={() => canNext && setTestimonialIndex(i => i + 1)}
            >
              <img src={`${ICO}/chevron-right.png`} alt="" />
            </button>
          </div>
        </div>
      </section>

      {/* ── CONTACT FORM ── */}
      <section className="contact-section" id="contact">
        <div className="container container--narrow">
          <div className="section-label-center">
            <SectionBadge text={C.contactBadge} />
          </div>
          <h2 className="section-title center">{C.contactTitle}</h2>
          <form className="contact-form" onSubmit={e => e.preventDefault()}>
            <div className="contact-field" ref={countryRef}>
              <label className="contact-label">{C.countryLabel}</label>
              <div
                className={`contact-select-wrap${focusedField === 'country' ? ' contact-select-wrap--active' : ''}`}
                onClick={() => { setFocusedField('country'); setCountryOpen(prev => !prev); }}
              >
                <span className="contact-flag">{selectedCountry.flag}</span>
                <span className="contact-select-value">{C.countries[selectedCountry.key]}</span>
                <img src={`${ICO}/dropdown.png`} alt="" className={`contact-select-chevron${countryOpen ? ' contact-select-chevron--open' : ''}`} />
              </div>
              {countryOpen && (
                <div className="contact-dropdown">
                  {COUNTRY_META.map((c) => (
                    <div
                      key={c.code}
                      className={`contact-dropdown-item${selectedCountry.key === c.key ? ' contact-dropdown-item--selected' : ''}`}
                      onClick={() => { setSelectedCountry(c); setCountryOpen(false); }}
                    >
                      <span className="contact-flag">{c.flag}</span>
                      <span>{C.countries[c.key]}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="contact-field">
              <label className="contact-label">Email</label>
              <div className="contact-input contact-input--display">{selectedCountry.email}</div>
            </div>
            <div className="contact-field">
              <label className="contact-label">{C.phoneLabel}</label>
              <div className="contact-phone-wrap">
                <span className="contact-phone-prefix">{selectedCountry.code}</span>
                <div className="contact-input contact-input--phone contact-input--display">{selectedCountry.phone}</div>
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="faq-section" id="faq">
        <div className="container container--narrow">
          <div className="section-label-center">
            <div className="section-badge">
              <img src={`${IMG}/stars.png`} alt="" className="section-badge-star-img" />
              <span>{C.faqBadge}</span>
            </div>
          </div>
          <h2 className="section-title center">{C.faqTitle}</h2>
          <div className="faq-list">
            {C.faqs.map((f, i) => (
              <div key={i} className={`faq-item ${openFaq === i ? 'faq-item--open' : ''}`}>
                <button
                  className="faq-question"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span>{f.q}</span>
                  <div className="faq-toggle">
                    <img src={`${ICO}/Plus.png`} alt="+" className="faq-toggle-icon" />
                  </div>
                </button>
                {openFaq === i && <p className="faq-answer">{f.a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cta-section" id="download">
        <div className="container">
          <div className="cta-card">
            <div className="cta-content">
              <p className="cta-eyebrow">{C.ctaEyebrow}</p>
              <h2 className="cta-title">{C.ctaTitle}</h2>
              <p className="cta-sub">{C.ctaSub}</p>
              <a href="#download"><MaxItBadge className="maxit-badge--light" small={C.maxitSmall} /></a>
            </div>
            <div className="cta-deco" aria-hidden="true">
              <img src={process.env.PUBLIC_URL + "/assets/Mask.png"} alt="" className="cta-mask" />
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="footer">
        <div className="footer-inner">
          <img src={LOGO_COLOR} alt="FlyAfritrip" className="footer-logo" />
          <span className="footer-copy">{tr('footerCopy')}</span>
          <div className="footer-links">
            <a href="#">{tr('footerTerms')}</a>
            <span>|</span>
            <a href="#">{tr('footerPrivacy')}</a>
            <span>|</span>
            <a href="#">{tr('footerContact')}</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
